import React, { useState, useEffect, useMemo } from 'react';
import {
  getSyncJobCostCurrentSummary,
  getSyncJobCostSummary,
  getSyncJobCostPhases,
  getSyncJobCostMonthlySummary,
  getProjects,
} from '../api';
import { useTheme } from '../hooks/useTheme';

/* ═══ Color Palette (matches WorkloadPage) ═══ */
const P = {
  indigo: '#6366f1', indigoL: '#a5b4fc', indigoBg: 'rgba(99,102,241,.08)',
  emerald: '#10b981', emeraldL: '#6ee7b7', emeraldBg: 'rgba(16,185,129,.08)',
  amber: '#f59e0b', amberL: '#fcd34d', amberBg: 'rgba(245,158,11,.08)',
  rose: '#f43f5e', roseL: '#fda4af', roseBg: 'rgba(244,63,94,.08)',
  sky: '#0ea5e9', skyBg: 'rgba(14,165,233,.08)',
};

/* ═══ Formatters ═══ */
const $f = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(+v || 0);
const $s = (v) => { const n = +v || 0; if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`; return `$${n.toFixed(0)}`; };
const fmtPct = (v) => { if (v === null || v === undefined) return '0%'; return parseFloat(v).toFixed(1) + '%'; };

const healthColor = (pctSpent) => {
  const pct = parseFloat(pctSpent || 0);
  if (pct >= 100) return P.rose;
  if (pct >= 85) return P.amber;
  return P.emerald;
};

const healthBg = (pctSpent) => {
  const pct = parseFloat(pctSpent || 0);
  if (pct >= 100) return P.roseBg;
  if (pct >= 85) return P.amberBg;
  return P.emeraldBg;
};

const healthLabel = (pctSpent) => {
  const pct = parseFloat(pctSpent || 0);
  if (pct >= 100) return 'Over Budget';
  if (pct >= 85) return 'At Risk';
  if (pct >= 50) return 'On Track';
  return 'Under Budget';
};

const overrunColor = (val) => parseFloat(val || 0) > 0 ? P.rose : P.emerald;

/* ═══ Budget Utilization Bar ═══ */
const BudgetBar = ({ budget, cost, committed, t }) => {
  const b = parseFloat(budget || 0);
  const c = parseFloat(cost || 0);
  const cm = parseFloat(committed || 0);
  const total = c + cm;
  const hasBudget = b > 0;
  const pctCost = hasBudget ? Math.min((c / b) * 100, 120) : 0;
  const pctCommitted = hasBudget ? Math.min((cm / b) * 100, 120 - pctCost) : 0;
  const ratio = hasBudget ? total / b : 0;
  const barColor = ratio >= 1 ? P.rose : ratio >= 0.85 ? P.amber : P.emerald;

  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: hasBudget ? barColor : t.textMuted }}>
          {$s(c)}
        </span>
        <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 600 }}>/</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: t.textSecondary }}>
          {hasBudget ? $s(b) : '–'}
        </span>
      </div>
      <div style={{
        width: '100%', height: 8, backgroundColor: t.dark ? '#2d3748' : '#e2e8f0',
        borderRadius: 4, overflow: 'hidden', position: 'relative',
      }}>
        {hasBudget && (
          <>
            <div style={{
              width: `${Math.min(pctCost, 100)}%`, height: '100%',
              backgroundColor: barColor, borderRadius: 4,
              transition: 'width 0.4s ease',
              position: 'absolute', left: 0, top: 0,
            }} />
            {pctCommitted > 0 && (
              <div style={{
                width: `${Math.min(pctCommitted, 100 - pctCost)}%`, height: '100%',
                backgroundColor: barColor, opacity: 0.35, borderRadius: '0 4px 4px 0',
                transition: 'width 0.4s ease',
                position: 'absolute', left: `${Math.min(pctCost, 100)}%`, top: 0,
              }} />
            )}
          </>
        )}
        {pctCost + pctCommitted > 100 && (
          <div style={{
            position: 'absolute', right: 0, top: 0, bottom: 0,
            width: `${Math.min(pctCost + pctCommitted - 100, 20)}%`,
            background: `repeating-linear-gradient(45deg, transparent, transparent 2px, ${P.rose}33 2px, ${P.rose}33 4px)`,
            borderRadius: '0 4px 4px 0',
          }} />
        )}
      </div>
      {hasBudget && (
        <div style={{ fontSize: 10, fontWeight: 600, color: barColor, marginTop: 2 }}>
          {ratio >= 1 ? `Over by ${$s(total - b)}` : `${Math.round((c / b) * 100)}% spent`}
          {cm > 0 && ` · ${$s(cm)} committed`}
        </div>
      )}
    </div>
  );
};

/* ═══ Mini Trend Chart (sparkline for monthly cost) ═══ */
const MiniTrend = ({ data, t }) => {
  if (!data || data.length < 2) return null;
  const h = 36, w = 100;
  const maxVal = Math.max(...data.map(d => parseFloat(d.total_cost || 0)), 1);
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (parseFloat(d.total_cost || 0) / maxVal) * (h - 4);
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} style={{ flexShrink: 0 }}>
      <polyline points={points.join(' ')} fill="none" stroke={P.indigo} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {data.length > 0 && (() => {
        const last = data[data.length - 1];
        const x = w;
        const y = h - (parseFloat(last.total_cost || 0) / maxVal) * (h - 4);
        return <circle cx={x} cy={y} r={3} fill={P.indigo} stroke={t.bg} strokeWidth={1.5} />;
      })()}
    </svg>
  );
};

/* ═══ Progress Ring ═══ */
const MiniRing = ({ pct, size = 36, stroke = 4, color = P.indigo, t }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const filled = Math.min(Math.max(pct || 0, 0), 100);
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={t.dark ? '#2d3748' : '#e2e8f0'} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={circ - (filled / 100) * circ}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset .5s ease' }} />
    </svg>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const JobCostDashboardPage = ({ onOpenProject, currentUser }) => {
  const t = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobSummaries, setJobSummaries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expandedJob, setExpandedJob] = useState(null);
  const [phaseCache, setPhaseCache] = useState({});
  const [trendCache, setTrendCache] = useState({});
  const [sortBy, setSortBy] = useState('overrun'); // overrun | budget | spent | name
  const [filterHealth, setFilterHealth] = useState('all'); // all | over | atrisk | ontrack

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [currentData, monthlyData, projectList] = await Promise.all([
        getSyncJobCostCurrentSummary({}).catch(() => []),
        getSyncJobCostSummary({}).catch(() => []),
        getProjects().catch(() => []),
      ]);
      const summaries = currentData.length > 0 ? currentData : monthlyData;
      setJobSummaries(summaries);
      setProjects(Array.isArray(projectList) ? projectList : projectList?.projects || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Expand a job card — load phases + trend if not cached
  const handleExpand = async (jobId) => {
    if (expandedJob === jobId) { setExpandedJob(null); return; }
    setExpandedJob(jobId);
    if (!phaseCache[jobId]) {
      try {
        const phases = await getSyncJobCostPhases({ job_id: jobId, source: 'current' });
        setPhaseCache(prev => ({ ...prev, [jobId]: phases }));
      } catch (err) { console.error('Phase load failed:', err); }
    }
    if (!trendCache[jobId]) {
      try {
        const trend = await getSyncJobCostMonthlySummary(jobId);
        setTrendCache(prev => ({ ...prev, [jobId]: trend }));
      } catch (err) { console.error('Trend load failed:', err); }
    }
  };

  // Project lookup
  const projectLookup = useMemo(() => {
    const map = {};
    projects.forEach(p => {
      if (p.project_number) map[p.project_number] = p;
      if (p.ce_job_id) map[p.ce_job_id] = p;
    });
    return map;
  }, [projects]);

  // Aggregate totals
  const totals = useMemo(() => {
    const r = { budget: 0, cost: 0, committed: 0, overrun: 0, jobCount: jobSummaries.length, overBudget: 0, atRisk: 0, onTrack: 0 };
    jobSummaries.forEach(j => {
      r.budget += parseFloat(j.total_budget || 0);
      r.cost += parseFloat(j.total_cost || 0);
      r.committed += parseFloat(j.total_committed || 0);
      r.overrun += parseFloat(j.total_overrun || 0);
      const pct = parseFloat(j.percent_spent || 0);
      if (pct >= 100) r.overBudget++;
      else if (pct >= 85) r.atRisk++;
      else r.onTrack++;
    });
    r.percentSpent = r.budget > 0 ? (100 * r.cost / r.budget) : 0;
    return r;
  }, [jobSummaries]);

  // Filter + sort
  const filteredJobs = useMemo(() => {
    let list = [...jobSummaries];
    if (filterHealth === 'over') list = list.filter(j => parseFloat(j.percent_spent || 0) >= 100);
    else if (filterHealth === 'atrisk') list = list.filter(j => { const p = parseFloat(j.percent_spent || 0); return p >= 85 && p < 100; });
    else if (filterHealth === 'ontrack') list = list.filter(j => parseFloat(j.percent_spent || 0) < 85);

    if (sortBy === 'overrun') list.sort((a, b) => parseFloat(b.total_overrun || 0) - parseFloat(a.total_overrun || 0));
    else if (sortBy === 'budget') list.sort((a, b) => parseFloat(b.total_budget || 0) - parseFloat(a.total_budget || 0));
    else if (sortBy === 'spent') list.sort((a, b) => parseFloat(b.percent_spent || 0) - parseFloat(a.percent_spent || 0));
    else if (sortBy === 'name') list.sort((a, b) => (a.job_name || '').localeCompare(b.job_name || ''));

    return list;
  }, [jobSummaries, filterHealth, sortBy]);

  /* ═══ Styles (matches WorkloadPage) ═══ */
  const card = {
    background: t.bg, borderRadius: 16,
    border: `1px solid ${t.cardBorder}`, padding: '24px 28px',
    marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)',
  };
  const kpiCard = (color, bg) => ({
    ...card, padding: '20px 24px', marginBottom: 0, flex: '1 1 180px', minWidth: 160,
    borderLeft: `4px solid ${color}`, background: t.dark ? t.bg : bg,
  });
  const pillBtn = (active) => ({
    padding: '6px 16px', borderRadius: 20, border: 'none',
    background: active ? P.indigo : 'transparent',
    color: active ? '#fff' : t.textSecondary,
    fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s',
  });

  if (loading) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{
        width: 44, height: 44, border: `3px solid ${t.cardBorder}`,
        borderTopColor: P.indigo, borderRadius: '50%', margin: '0 auto 16px',
        animation: 'spin .7s linear infinite',
      }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: t.textSecondary, fontSize: 14, fontWeight: 500 }}>Loading job cost data...</div>
    </div>
  );

  if (error) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ color: P.rose, fontSize: 14 }}>{error}</div>
      <button onClick={loadData} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, border: 'none', background: P.indigo, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Retry</button>
    </div>
  );

  if (jobSummaries.length === 0) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: t.heading, marginBottom: 8 }}>No Cost Data Available</div>
      <div style={{ fontSize: 13, color: t.textMuted }}>No synced job cost data found. Run a CE Agent pull to populate data.</div>
    </div>
  );

  const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: t.heading, letterSpacing: '-.4px' }}>
            Job Costs
          </h2>
          <div style={{ fontSize: 12, color: t.textSecondary, marginTop: 6 }}>
            Budget tracking and cost analysis across all ComputerEase jobs
          </div>
        </div>
        <button onClick={loadData} style={{
          padding: '8px 18px', borderRadius: 10, border: `1px solid ${t.cardBorder}`,
          background: t.bg, color: t.text, fontWeight: 600, fontSize: 13, cursor: 'pointer',
        }}>
          Refresh
        </button>
      </div>

      {/* KPI Summary Cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={kpiCard(P.indigo, P.indigoBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Active Jobs</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.indigo, marginTop: 4 }}>{totals.jobCount}</div>
        </div>
        <div style={kpiCard(P.emerald, P.emeraldBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Total Budget</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.emerald, marginTop: 4 }}>{$s(totals.budget)}</div>
        </div>
        <div style={kpiCard(P.sky, P.skyBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Total Spent</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.sky, marginTop: 4 }}>{$s(totals.cost)}</div>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{fmtPct(totals.percentSpent)} of budget</div>
        </div>
        <div style={kpiCard(P.amber, P.amberBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Committed (POs)</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.amber, marginTop: 4 }}>{$s(totals.committed)}</div>
        </div>
        <div style={kpiCard(totals.overrun > 0 ? P.rose : P.emerald, totals.overrun > 0 ? P.roseBg : P.emeraldBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Net Over/Under</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: totals.overrun > 0 ? P.rose : P.emerald, marginTop: 4 }}>{$s(totals.overrun)}</div>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
            {totals.overBudget} over · {totals.atRisk} at risk · {totals.onTrack} on track
          </div>
        </div>
      </div>

      {/* Filter & Sort Bar */}
      <div style={{ ...card, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 4, background: t.bgAlt, borderRadius: 24, padding: 3 }}>
          {[
            { k: 'all', l: 'All Jobs' },
            { k: 'over', l: `Over Budget (${totals.overBudget})` },
            { k: 'atrisk', l: `At Risk (${totals.atRisk})` },
            { k: 'ontrack', l: `On Track (${totals.onTrack})` },
          ].map(f => (
            <button key={f.k} style={pillBtn(filterHealth === f.k)} onClick={() => setFilterHealth(f.k)}>{f.l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', letterSpacing: .5 }}>Sort by</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '6px 12px', borderRadius: 8, border: `1px solid ${t.cardBorder}`,
              background: t.inputBg, color: t.inputColor, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <option value="overrun">Most Over Budget</option>
            <option value="budget">Largest Budget</option>
            <option value="spent">% Spent</option>
            <option value="name">Job Name</option>
          </select>
        </div>
      </div>

      {/* Job Cards */}
      {filteredJobs.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 40, color: t.textMuted }}>
          No jobs match the current filter.
        </div>
      ) : (
        filteredJobs.map(job => {
          const isExpanded = expandedJob === job.job_id;
          const pctSpent = parseFloat(job.percent_spent || 0);
          const overrun = parseFloat(job.total_overrun || 0);
          const proj = projectLookup[job.job_id];
          const phases = phaseCache[job.job_id] || [];
          const trend = trendCache[job.job_id] || [];

          return (
            <div key={job.job_id} style={{ ...card, padding: 0, overflow: 'hidden' }}>
              {/* Job Header Row */}
              <div
                onClick={() => handleExpand(job.job_id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.5fr auto auto auto auto auto auto',
                  gap: 20, padding: '20px 24px', alignItems: 'center', cursor: 'pointer',
                  transition: 'background .15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.dark ? 'rgba(255,255,255,0.02)' : '#fafbfc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {/* Job name + health badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: `linear-gradient(135deg, ${healthColor(pctSpent)}, ${healthColor(pctSpent)}88)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0,
                  }}>
                    {(job.job_id || '??').slice(0, 3).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: t.heading }}>
                      {job.job_name || job.job_id}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 600 }}>{job.job_id}</span>
                      <span style={{
                        padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                        color: healthColor(pctSpent), background: healthBg(pctSpent),
                      }}>
                        {healthLabel(pctSpent)}
                      </span>
                      {proj && (
                        <span
                          onClick={(e) => { e.stopPropagation(); onOpenProject && onOpenProject(proj.id); }}
                          style={{
                            padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                            color: P.indigo, background: P.indigoBg, cursor: 'pointer',
                          }}
                        >
                          Open Project →
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Budget utilization bar */}
                <BudgetBar budget={job.total_budget} cost={job.total_cost} committed={job.total_committed} t={t} />

                {/* % Spent ring */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MiniRing pct={Math.min(pctSpent, 100)} color={healthColor(pctSpent)} t={t} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>{fmtPct(pctSpent)}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase' }}>Spent</div>
                  </div>
                </div>

                {/* Over/Under */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: overrunColor(overrun) }}>
                    {$s(Math.abs(overrun))}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase' }}>
                    {overrun > 0 ? 'Over' : 'Under'}
                  </div>
                </div>

                {/* Trend sparkline */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {trendCache[job.job_id] && trendCache[job.job_id].length >= 2 ? (
                    <MiniTrend data={trendCache[job.job_id]} t={t} />
                  ) : (
                    <div style={{ width: 100, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 10, color: t.textMuted }}>–</span>
                    </div>
                  )}
                  <div style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', marginTop: 2 }}>Trend</div>
                </div>

                {/* Phases count */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: t.heading }}>
                    {phaseCache[job.job_id] ? phaseCache[job.job_id].length : '–'}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase' }}>Phases</div>
                </div>

                {/* Expand chevron */}
                <div style={{ fontSize: 14, color: t.textMuted, transition: 'transform .2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
                  ▾
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{
                  borderTop: `1px solid ${t.cardBorder}`,
                  background: t.dark ? 'rgba(255,255,255,0.01)' : '#fafbfc',
                  padding: '16px 24px',
                }}>
                  {/* Summary stats row */}
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Budget:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>{$f(job.total_budget)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Actual Cost:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>{$f(job.total_cost)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Committed:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: P.amber }}>{$f(job.total_committed)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Over/Under:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: overrunColor(job.total_overrun) }}>{$f(job.total_overrun)}</span>
                    </div>
                    {job.report_date && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Data as of:</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>
                          {new Date(job.report_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Monthly Trend Chart */}
                  {trend.length > 1 && (
                    <div style={{
                      marginBottom: 20, padding: '16px 20px',
                      background: t.bg, borderRadius: 12, border: `1px solid ${t.cardBorder}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: t.heading }}>Monthly Cost Trend</h4>
                        <div style={{ display: 'flex', gap: 16, fontSize: 11, fontWeight: 600 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: P.indigo, opacity: 0.3, display: 'inline-block' }} />
                            <span style={{ color: t.textMuted }}>Monthly Cost</span>
                          </span>
                        </div>
                      </div>
                      {/* Bar chart */}
                      {(() => {
                        const chartH = 140;
                        const barW = Math.max(24, Math.min(48, 500 / trend.length));
                        const chartW = Math.max(trend.length * (barW + 12) + 40, 300);
                        const maxCost = Math.max(...trend.map(m => parseFloat(m.total_cost || 0)), 1);
                        const padTop = 20, padBot = 30;
                        const usable = chartH - padTop - padBot;

                        return (
                          <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
                            <svg width={chartW} height={chartH} style={{ display: 'block' }}>
                              {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
                                <line key={i} x1={20} x2={chartW} y1={padTop + usable * (1 - pct)} y2={padTop + usable * (1 - pct)}
                                  stroke={t.dark ? '#2d3748' : '#f1f5f9'} strokeWidth={1} />
                              ))}
                              {trend.map((m, i) => {
                                const x = i * (barW + 12) + 20;
                                const cost = parseFloat(m.total_cost || 0);
                                const h = (cost / maxCost) * usable;
                                return (
                                  <g key={i}>
                                    <rect x={x} y={padTop + usable - h} width={barW} height={h}
                                      rx={4} fill={P.indigo} opacity={0.25} />
                                    <rect x={x} y={padTop + usable - h} width={barW} height={Math.min(h, 4)}
                                      rx={2} fill={P.indigo} opacity={0.6} />
                                    <text x={x + barW / 2} y={padTop + usable - h - 4}
                                      textAnchor="middle" fontSize={9} fontWeight={700} fill={P.indigo}>
                                      {cost > 0 ? $s(cost) : ''}
                                    </text>
                                    <text x={x + barW / 2} y={chartH - 4}
                                      textAnchor="middle" fontSize={9} fontWeight={600} fill={t.textMuted}>
                                      {monthNames[m.period_month]} '{String(m.period_year).slice(-2)}
                                    </text>
                                  </g>
                                );
                              })}
                            </svg>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Phase Breakdown Table */}
                  {phases.length > 0 && (
                    <>
                      <div style={{
                        display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 1fr 1fr',
                        gap: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5,
                        color: t.textMuted, padding: '8px 12px', borderBottom: `1px solid ${t.cardBorder}`,
                      }}>
                        <div>Phase</div>
                        <div style={{ textAlign: 'right' }}>Budget</div>
                        <div style={{ textAlign: 'right' }}>Actual Cost</div>
                        <div style={{ textAlign: 'right' }}>Committed</div>
                        <div style={{ textAlign: 'right' }}>Over/Under</div>
                        <div style={{ textAlign: 'right' }}>% Spent</div>
                        <div style={{ textAlign: 'center' }}>Status</div>
                      </div>
                      {phases.map(phase => {
                        const phaseOverrun = parseFloat(phase.total_overrun || 0);
                        const phasePct = parseFloat(phase.percent_spent || 0);
                        return (
                          <div
                            key={phase.phase}
                            style={{
                              display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 1fr 1fr',
                              gap: 0, padding: '12px', alignItems: 'center', fontSize: 13,
                              borderBottom: `1px solid ${t.cardBorder}`,
                              transition: 'background .15s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.dark ? 'rgba(255,255,255,0.03)' : '#f1f5f9'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div>
                              <div style={{ fontWeight: 600, color: t.heading }}>{phase.phase_name || phase.phase}</div>
                              <div style={{ fontSize: 11, color: t.textMuted }}>Phase {phase.phase} · {phase.category_count} categories</div>
                            </div>
                            <div style={{ textAlign: 'right', fontWeight: 700, color: t.heading }}>{$f(phase.total_budget)}</div>
                            <div style={{ textAlign: 'right', fontWeight: 600, color: t.text }}>{$f(phase.total_cost)}</div>
                            <div style={{ textAlign: 'right', fontWeight: 600, color: P.amber }}>{$f(phase.total_committed)}</div>
                            <div style={{ textAlign: 'right', fontWeight: 700, color: overrunColor(phaseOverrun) }}>
                              {$f(phase.total_overrun)}
                            </div>
                            <div style={{ textAlign: 'right', fontWeight: 700, color: healthColor(phasePct) }}>
                              {fmtPct(phasePct)}
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <span style={{
                                padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                                color: healthColor(phasePct), background: healthBg(phasePct),
                              }}>
                                {healthLabel(phasePct)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Loading indicator for phases */}
                  {!phaseCache[job.job_id] && (
                    <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: t.textMuted }}>
                      Loading phase breakdown...
                    </div>
                  )}
                  {phaseCache[job.job_id] && phases.length === 0 && (
                    <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: t.textMuted }}>
                      No phase data available for this job.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Data note */}
      {jobSummaries[0]?.report_date && (
        <div style={{ fontSize: 11, color: t.textMuted, textAlign: 'right', marginTop: 4 }}>
          Data as of {new Date(jobSummaries[0].report_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      )}
    </div>
  );
};

export default JobCostDashboardPage;
